// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { shouldMountOpticalGlass } from '@/rendering/appSurfacePolicy';
import { useAppSurfacePolicy } from '@/rendering/useAppSurfacePolicy';

/**
 * 批次 8 收口：把 sonner Toaster 挂进「已有页面舞台」，让 toast 也吃到真玻璃。
 *
 * 为什么不是给每条 toast 配一个自包含小舞台：sonner 的 144 处调用点里绝大多数
 * （success/error/warning/info）由 sonner 自己渲染内容，无法逐条注入组件；而 toast
 * 位于视口顶部，页面舞台（通常整屏）已经盖住这块区域 —— 挂进去即可零新建上下文地
 * 拿到真实折射（toast 是 `position: fixed`，视觉位置不受父级影响）。
 *
 * 落位规则：优先"完整包含 toast 区域（顶部中央 480×min(360, 视口高)）的最大已就绪舞台"；
 * 以下两种情况**就地渲染**（toast 回落 CSS 毛玻璃，绝不出现半玻璃半透明）：
 *   1. 舞台在页面顶部盖不住 toast 区域（营销首屏、登录页把舞台放在右侧面板）；
 *   2. 当前有弹层打开（`[data-glass-portal-host]`）——弹层在 body 层且 z-index 更高，
 *      toast 挂进舞台会被压在弹层下面，宁可退回顶层 + CSS 毛玻璃。
 */

const TOAST_SPAN_X = 480;
const TOAST_SPAN_Y = 360;

export function resolveGlassToasterHost(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  // 弹层打开时不挂舞台：保证 toast 始终在弹层之上可见
  if (document.querySelector('[data-glass-portal-host]')) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = vw / 2 - TOAST_SPAN_X / 2;
  const right = vw / 2 + TOAST_SPAN_X / 2;
  const bottom = Math.min(TOAST_SPAN_Y, vh);

  let best: HTMLElement | null = null;
  let bestArea = 0;
  for (const element of document.querySelectorAll<HTMLElement>('.lg-stage.webgl-ready')) {
    const rect = element.getBoundingClientRect();
    const covers = rect.left <= left && rect.right >= right && rect.top <= 1 && rect.bottom >= bottom;
    if (!covers) continue;
    const area = rect.width * rect.height;
    if (area > bestArea) {
      bestArea = area;
      best = element;
    }
  }
  return best;
}

/**
 * 普通 Toaster 宿主（T04 / 批次性能优化）：工作区不启动「查找玻璃舞台」的
 * MutationObserver 与 2 秒轮询，也不创建 portal —— 直接把 sonner 挂在 body 顶层。
 */
function PlainToasterLayer({ children }: { children: ReactNode }) {
  return createPortal(children, document.body);
}

export default function GlassToasterLayer({ children }: { children: ReactNode }) {
  const surfacePolicy = useAppSurfacePolicy();
  // Hook 顺序固定：策略分支只影响内容，不条件调用 Hook。
  const opticalEnabled = shouldMountOpticalGlass(surfacePolicy);
  return opticalEnabled ? <GlassToasterHost>{children}</GlassToasterHost> : <PlainToasterLayer>{children}</PlainToasterLayer>;
}

function GlassToasterHost({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const hostRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let disposed = false;
    let timer = 0;

    const validHost = () =>
      !!hostRef.current?.isConnected &&
      hostRef.current.classList.contains('webgl-ready') &&
      // 弹层打开时不驻留舞台（见文件头落位规则 2）
      !document.querySelector('[data-glass-portal-host]');

    const resolve = () => {
      if (disposed) return;
      if (validHost()) return; // 已就位：宿主还在文档里就是路由切换后的正确舞台
      const next = resolveGlassToasterHost();
      if (next !== hostRef.current) {
        hostRef.current = next;
        setHost(next);
      }
    };

    const schedule = () => {
      if (disposed || validHost()) return; // 有宿主时完全不做判定，避免流式渲染期空转
      window.clearTimeout(timer);
      timer = window.setTimeout(resolve, 320); // 舞台异步进入 webgl-ready，略等一拍
    };

    resolve();
    const retries = [800, 1800, 3200].map((delay) => window.setTimeout(resolve, delay));
    // 自愈轮询：路由/标签切换期间舞台会重建与动画，可能错过一次判定；有宿主时空转（廉价）
    const interval = window.setInterval(() => {
      if (!validHost()) resolve();
    }, 2000);

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-glass-portal-host'], // 舞台 ready / 弹层开关
    });

    window.addEventListener('resize', schedule);
    return () => {
      disposed = true;
      window.clearTimeout(timer);
      window.clearInterval(interval);
      retries.forEach((id) => window.clearTimeout(id));
      observer.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, []);

  if (!host) return <>{children}</>;
  return createPortal(children, host);
}
